import React, { useEffect } from 'react'
import ConsentBanner from '../components/ConsentBanner'
import ContactForm from '../components/ContactForm'
import { track } from '../services/analytics'

const SEO_TITLE = 'LogiSelect | Online TMS for Australian Transport & Logistics Operators'
const SEO_DESCRIPTION =
  'LogiSelect is an online TMS (Transport Management System) built for small and mid-market Australian transport and logistics businesses. Manage planning, invoicing, groupage, LTL and FTL freight, and owner-driver fleets with local, personal support from $1 per consignment.'
const SEO_KEYWORDS =
  'TMS, online TMS, transport management system, mid market logistics, invoicing, planning, groupage, LTL, less than truckload, FTL, full truckload, owner-driver, Australia, local transport, personal service'

export default function PublicWebsitePage() {
  const AUTO_LOGIN_FLAG = 'logiselect-autologin'
  const TMS_BASE_URL = String(import.meta.env.VITE_TMS_BASE_URL || 'https://logiselect.net').replace(/\/$/, '')
  // Include ?autologin=1 so the flag survives the cross-origin hop
  // (sessionStorage is per-origin and would be lost between the website and TMS domains).
  const tmsLoginUrl = `${TMS_BASE_URL}/login?autologin=1`
  const ownershipFooterText = '© 2026 LogiSelect Pty Ltd. All content on this website and all TMS software are owned by LogiSelect Pty Ltd.'

  useEffect(() => {
    const previousTitle = document.title
    document.title = SEO_TITLE

    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }

    setMeta('name', 'description', SEO_DESCRIPTION)
    setMeta('name', 'keywords', SEO_KEYWORDS)
    setMeta('property', 'og:title', SEO_TITLE)
    setMeta('property', 'og:description', SEO_DESCRIPTION)

    const ldJsonId = 'logiselect-public-website-jsonld'
    let ldScript = document.getElementById(ldJsonId) as HTMLScriptElement | null
    if (!ldScript) {
      ldScript = document.createElement('script')
      ldScript.type = 'application/ld+json'
      ldScript.id = ldJsonId
      document.head.appendChild(ldScript)
    }
    ldScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'LogiSelect',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: SEO_DESCRIPTION,
      offers: {
        '@type': 'Offer',
        price: '1.00',
        priceCurrency: 'AUD',
        description: 'Pay-per-use pricing: $1 AUD per consignment created in the TMS.',
      },
      areaServed: 'Australia',
      provider: { '@type': 'Organization', name: 'LogiSelect Pty Ltd' },
    })

    return () => {
      document.title = previousTitle
    }
  }, [])

  const handleOpenTmsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    window.alert('Warning: The LogiSelect TMS demo database is renewed every Sunday at midnight AEST.')
    sessionStorage.setItem(AUTO_LOGIN_FLAG, '1')
    track('cta_click', { cta: 'open_tms_demo' })
    window.location.href = tmsLoginUrl
  }

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f6f7f3 0%, #ffffff 42%, #eef2f6 100%)',
    color: '#0f172a',
    position: 'relative',
  }

  const largeLogoPanel: React.CSSProperties = {
    position: 'absolute',
    left: 'max(18px, calc((100vw - 1200px) / 2 - 180px))',
    top: 18,
    width: 160,
    background: '#ffffff',
    border: '1px solid #dbe3ea',
    borderRadius: 20,
    boxShadow: '0 16px 34px rgba(15, 23, 42, 0.09)',
    padding: '22px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const shellStyle: React.CSSProperties = {
    width: 'min(1200px, calc(100% - 32px))',
    margin: '0 auto',
    padding: '0 0 48px',
  }

  const heroGrid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(320px, 420px)',
    gap: 28,
    alignItems: 'stretch',
  }

  const heroCard: React.CSSProperties = {
    background: 'radial-gradient(circle at top left, rgba(180, 213, 190, 0.55), rgba(255,255,255,0.96) 42%), linear-gradient(135deg, #fff 0%, #f3f6f8 100%)',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: 28,
    padding: '36px 34px',
    boxShadow: '0 22px 50px rgba(15, 23, 42, 0.08)',
  }

  const statCard: React.CSSProperties = {
    background: '#f8fafc',
    border: '1px solid #dbe4ec',
    borderRadius: 18,
    padding: '16px 18px',
  }

  const sectionCard: React.CSSProperties = {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 24,
    padding: '28px 28px 30px',
    boxShadow: '0 16px 34px rgba(15, 23, 42, 0.06)',
  }

  const tmsCard: React.CSSProperties = {
    background: '#fff',
    borderRadius: 24,
    padding: '32px 28px',
    border: '1px solid #dbe3ea',
    boxShadow: '0 24px 50px rgba(15, 23, 42, 0.12)',
    alignSelf: 'start',
    position: 'sticky',
    top: 20,
  }

  const companyInfoCard: React.CSSProperties = {
    background: '#fff',
    borderRadius: 24,
    padding: '28px 28px',
    border: '1px solid #dbe3ea',
    boxShadow: '0 24px 50px rgba(15, 23, 42, 0.12)',
    alignSelf: 'start',
  }

  const ctaPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '13px 18px',
    borderRadius: 999,
    background: '#16324f',
    color: '#ffffff',
    textDecoration: 'none',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    minWidth: 180,
  }

  const ctaSecondary: React.CSSProperties = {
    ...ctaPrimary,
    background: '#ffffff',
    color: '#16324f',
    border: '1px solid #bfd0dc',
  }

  const ctaRow: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
    marginTop: 26,
  }

  const ctaCell: React.CSSProperties = {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  }

  return (
    <div style={pageStyle} className="pw-page">
      <div style={largeLogoPanel} className="pw-logo-panel">
        <img src="/logiselect-logo.png" alt="LogiSelect - Online TMS for Australian transport, groupage, LTL, FTL and owner-driver operators" style={{ width: '100%', height: 'auto', maxWidth: 250, display: 'block' }} />
      </div>
      <div style={shellStyle} className="pw-shell">
        <div style={{ paddingTop: 18 }} />

        <div style={heroGrid} className="pw-hero-grid">
          <div style={{ display: 'grid', gap: 24 }}>
            <section style={heroCard} className="pw-hero-card">
              
              <h1 style={{ margin: 0, fontSize: 'clamp(2.1rem, 4vw, 4rem)', lineHeight: 1, letterSpacing: '-0.04em', maxWidth: 780 }}>
                Online TMS for small and mid-market transport &amp; logistics operators in Australia.
              </h1>
              <p style={{ margin: '18px 0 0', fontSize: 18, lineHeight: 1.65, color: '#334155', maxWidth: 760 }}>
                LogiSelect is a transport management system (TMS) developed from more than 30 years of real transport management experience. It is built for local, personal service providers who need practical planning, consignment control, pricing, invoicing, and reporting in one online platform — whether you run groupage, LTL (less-than-truckload), FTL (full-truckload) linehaul, or an owner-driver fleet.
              </p>
              <p style={{ margin: '12px 0 0', fontSize: 16, lineHeight: 1.65, color: '#475569', maxWidth: 760 }}>
                The TMS link on this website opens a <strong>demo version</strong> of LogiSelect.
              </p>
              <div style={ctaRow} className="pw-cta-row">
                <a href={tmsLoginUrl} style={{ ...ctaPrimary, ...ctaCell }} onClick={handleOpenTmsClick}>Access LogiSelect TMS</a>
                <a href="/USER_MANUAL.pdf" download style={{ ...ctaSecondary, ...ctaCell }}>Download Manual</a>
                <a href="/terms-and-conditions" style={{ ...ctaSecondary, ...ctaCell }}>
                  The large fine prints!
                </a>
              </div>
            </section>

            <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
              <div style={statCard}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>Experience</div>
                <div style={{ fontSize: 28, fontWeight: 800, marginTop: 10 }}>30+ years</div>
                <div style={{ fontSize: 14, color: '#475569', marginTop: 8 }}>Built from transport operations knowledge, not generic software theory.</div>
              </div>
              <div style={statCard}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>Deployment</div>
                <div style={{ fontSize: 28, fontWeight: 800, marginTop: 10 }}>Online TMS</div>
                <div style={{ fontSize: 14, color: '#475569', marginTop: 8 }}>Accessible for growing logistics businesses that need speed without complexity.</div>
              </div>
              <div style={statCard}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>Pricing</div>
                <div style={{ fontSize: 28, fontWeight: 800, marginTop: 10 }}>$1</div>
                <div style={{ fontSize: 14, color: '#475569', marginTop: 8 }}>Simple charge per consignment created in the system.</div>
              </div>
            </section>

            <section style={sectionCard} className="pw-section-card">
              <h2 style={{ margin: 0, fontSize: 32, letterSpacing: '-0.03em' }}>Why LogiSelect</h2>
              <div style={{ display: 'grid', gap: 14, marginTop: 18, color: '#334155' }}>
                Traditional Transport Management Systems (TMS) lock smaller operations into rigid, high-cost monthly subscription tiers.
                LogiSelect disrupts the status quo by introducing a strict pay-per-use commercial model:
                <strong> Exactly $1.00 AUD per consignment registered in the TMS.</strong>
                <div><strong>Operational control:</strong> manage companies, addresses, consignments, routes, tariffs, invoices, and reporting in a single workflow.</div>
                <div><strong>Planning support:</strong> use route planning, linehaul, hub-spoke, and distance integrations for daily execution.</div>
                <div><strong>Practical AI assistance:</strong> ask questions in normal language and retrieve counts, totals, and operational records directly from the TMS.</div>
              </div>
            </section>

            <section style={sectionCard} className="pw-section-card">
              <h2 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.03em' }}>Platform Focus</h2>
              <p style={{ margin: '16px 0 0', color: '#334155', lineHeight: 1.8 }}>
                LogiSelect is designed specifically for small and mid-market fleet operations across Australia, providing the core administrative and logistical tools needed
                to manage a transport business without unnecessary enterprise bloat:
              </p>
              <div style={{ display: 'grid', gap: 12, marginTop: 16, color: '#334155' }}>
                <div><strong>Operations:</strong> Consignment entry, smart planning and routing, leg overview tracking, and visual stop sequences.</div>
                <div><strong>Freight types:</strong> Purpose-built for groupage, LTL (less-than-truckload) and FTL (full-truckload) linehaul, hub-spoke networks, and owner-driver fleets.</div>
                <div><strong>Resources:</strong> Dedicated portals for vehicle configuration, driver allocation, and standardized packaging definitions.</div>
                <div><strong>Finance &amp; Reporting:</strong> Invoicing automation, customizable charge codes, dynamic tariff modules, and a built-in report designer.</div>
                <div><strong>Administration:</strong> Fast data ingestion via an Import Center, role-based access controls, and custom web hook/API triggers.</div>
              </div>
            </section>

            <section style={sectionCard} className="pw-section-card">
              <h2 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.03em' }}>Built for Australian Transport &amp; Logistics Operators</h2>
              <p style={{ margin: '16px 0 0', color: '#334155', lineHeight: 1.8 }}>
                LogiSelect is an <strong>online TMS</strong> built for small and <strong>mid-market</strong> logistics providers across <strong>Australia</strong>.
                Whether you run <strong>groupage</strong>, <strong>LTL</strong> (less-than-truckload), <strong>FTL</strong> (full-truckload) linehaul, or an
                <strong> owner-driver</strong> fleet, LogiSelect gives you <strong>local</strong>, <strong>personal</strong> support alongside the
                <strong> planning</strong>, <strong>invoicing</strong>, and reporting tools you need to run a transport management business efficiently —
                without locking you into enterprise-grade subscription pricing.
              </p>
            </section>

            <section style={sectionCard} className="pw-section-card">
              <h2 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.03em' }}>Pricing</h2>
              <p style={{ margin: '16px 0 0', fontSize: 18, color: '#334155' }}>
                LogiSelect charges <strong>$1 per consignment</strong> in the system. You will have access to a Test and Production environment. 
                There are no limitations to the funcitonality, API in and out functionality and You can create your own invoices and reports. 
              </p>
            </section>

            <section style={sectionCard} className="pw-section-card">
              <h2 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.03em' }}>Manual</h2>
              <p style={{ margin: '16px 0 0', color: '#334155', lineHeight: 1.7 }}>
                Download the current user manual for LogiSelect to review core workflows, screen guidance, and operational functions.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 18 }}>
                <a href="/USER_MANUAL.pdf" download style={ctaPrimary}>Download User Manual</a>
                <a href={tmsLoginUrl} style={ctaSecondary} onClick={handleOpenTmsClick}>Go to TMS</a>
              </div>
            </section>
          </div>

          <div style={{ display: 'grid', gap: 28, alignContent: 'start' }}>
            <aside style={tmsCard} className="pw-tms-card">
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <img src="/logiselect-logo.png" alt="LogiSelect logo" style={{ height: 52, width: 'auto', display: 'inline-block', marginBottom: 8 }} />
                <h2 style={{ margin: 0, fontSize: 24, color: '#0f172a', fontWeight: 800 }}>LogiSelect TMS</h2>
                <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>Use the dedicated TMS link to sign in and work inside the system.</p>
              </div>
              <a href={tmsLoginUrl} style={{ ...ctaPrimary, width: '100%', boxSizing: 'border-box' }} onClick={handleOpenTmsClick}>Open TMS</a>
            </aside>

            <section style={companyInfoCard}>
              <h3 style={{ margin: 0, fontSize: 24, color: '#0f172a', fontWeight: 800 }}>LogiSelect Pty Ltd</h3>
              <div style={{ marginTop: 14, color: '#334155', lineHeight: 1.75, fontSize: 18 }}>
                <div>ACN: 700403345</div>
                <div>ABN: 62700403345</div>
                <div> </div>
                <div>
                  Contact:{' '}
                  <a href="mailto:info@logiselect.com.au" style={{ color: '#16324f', fontWeight: 700 }}>
                    info@logiselect.com.au
                  </a>
                </div>
                
              </div>
            </section>
            </div>
        </div>

        <section
          id="contact"
          style={{
            marginTop: 32,
            background: '#ffffff',
            borderRadius: 12,
            padding: '28px 24px',
            boxShadow: '0 4px 12px rgba(0,0,0,.06)',
          }}
        >
          <h2 style={{ marginTop: 0 }}>Talk to us</h2>
          <p style={{ marginTop: 0, color: '#374151' }}>
            Send a message and we'll get back to you within one business day.
          </p>
          <ContactForm />
        </section>

        <footer
          style={{
            marginTop: 28,
            textAlign: 'center',
            color: '#475569',
            fontSize: 13,
            lineHeight: 1.6,
            padding: '10px 8px 2px',
          }}
        >
          {ownershipFooterText}
        </footer>
      </div>
      <ConsentBanner />
    </div>
  )
}